import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-purple-800 mb-3">{title}</h2>
    <div className="space-y-3 text-gray-700 text-[16px] leading-relaxed">{children}</div>
  </div>
);

const PrivacyPage = () => (
  <div className="min-h-screen bg-white">
    <SEOHead title="Privacy Policy | Wanderloop" description="How Wanderloop collects, uses, and protects your data." />
    <Header />
    <main className="max-w-3xl mx-auto px-4 py-16">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
      <p className="text-sm text-gray-400 mb-10">Effective April 15, 2026</p>

      <Section title="What We Collect">
        <p>When you create an account, we collect your name and email address. If you sign in with Google, we receive your Google profile name and email.</p>
        <p>As you use Wanderloop, we store your walk activity: walks you save, lists you create, walks you mark as completed, and any community feedback you submit.</p>
        <p>We collect standard usage data including pages visited, time spent on the site, device type, and browser. This data is anonymized and used in aggregate to improve the product.</p>
      </Section>

      <Section title="How We Use Your Data">
        <p>We use your data to provide and improve Wanderloop: displaying your saved walks, personalizing recommendations, and making our walk content better based on how people actually use it.</p>
        <p>We send transactional emails (welcome, password reset, subscription confirmation) to the email on your account. We will never send marketing emails unless you explicitly opt in, and you can opt out at any time.</p>
        <p>We do not sell your personal data to anyone. We do not share your email with advertisers.</p>
      </Section>

      <Section title="Third-Party Services">
        <p>Wanderloop uses the following third-party services, each of which receives only the data necessary to provide its function:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Google Maps and Places API</strong> — displays walk routes and resolves location data. Google receives geographic coordinates and place queries. Subject to Google's privacy policy.</li>
          <li><strong>Firebase</strong> — handles authentication and file storage. Receives your email and login credentials (encrypted).</li>
          <li><strong>Unsplash</strong> — provides walk photos. No personal data is shared with Unsplash.</li>
          <li><strong>Stripe</strong> — processes Wanderloop+ subscription payments. Stripe receives your payment details directly. We do not see or store your card number.</li>
          <li><strong>Google Analytics</strong> — collects anonymized usage data (pages visited, session duration). No personally identifiable information is sent.</li>
          <li><strong>Resend</strong> — delivers transactional emails. Receives your email address for delivery purposes only.</li>
        </ul>
      </Section>

      <Section title="Cookies">
        <p>We use cookies for two purposes: managing your authentication session (keeping you logged in) and collecting anonymized analytics. We do not use advertising cookies or tracking pixels.</p>
      </Section>

      <Section title="Your Rights">
        <p>You can update your email and display name at any time from your account settings.</p>
        <p>You can request deletion of your account and all associated data by emailing hello@wanderloop.app. We will process deletion requests within 30 days.</p>
        <p>You can opt out of any non-transactional emails by clicking the unsubscribe link in any email or contacting us directly.</p>
      </Section>

      <Section title="Data Retention">
        <p>We retain your account data for as long as your account is active. If you request deletion, we remove your personal data within 30 days. Anonymized usage data may be retained indefinitely for product improvement.</p>
      </Section>

      <Section title="Contact">
        <p>For privacy questions or data requests, email <a href="mailto:hello@wanderloop.app" className="text-purple-700 underline">hello@wanderloop.app</a>.</p>
      </Section>
    </main>
    <Footer />
  </div>
);

export default PrivacyPage;
